import type { Icon } from '@rover/icons';
import Boarding from '@rover/icons/svg/boarding.svg';
import Daycare from '@rover/icons/svg/daycare.svg';
import DogTraining from '@rover/icons/svg/dog-training.svg';
import DropIn from '@rover/icons/svg/drop-in.svg';
import Grooming from '@rover/icons/svg/grooming.svg';
import Sitting from '@rover/icons/svg/sitting.svg';
import Walking from '@rover/icons/svg/walking.svg';
import type { AutomaticRecord } from '@rover/rsdk/src/apiClient/latest/model/automaticRecord';
import { AutomaticRecordTypeEnum } from '@rover/rsdk/src/apiClient/latest/model/automaticRecordTypeEnum';
import type { ExternalReview } from '@rover/rsdk/src/apiClient/latest/model/externalReview';
import { ExternalReviewTypeEnum } from '@rover/rsdk/src/apiClient/latest/model/externalReviewTypeEnum';
import type { ProviderRating } from '@rover/rsdk/src/apiClient/latest/model/providerRating';
import { ProviderRatingTypeEnum } from '@rover/rsdk/src/apiClient/latest/model/providerRatingTypeEnum';
import type { ReviewDispatch } from '@rover/rsdk/src/apiClient/latest/model/reviewDispatch';
import type { Testimonial } from '@rover/rsdk/src/apiClient/latest/model/testimonial';
import { TestimonialTypeEnum } from '@rover/rsdk/src/apiClient/latest/model/testimonialTypeEnum';
import type { TrainerReviewProxyHomogenized } from '@rover/rsdk/src/apiClient/latest/model/trainerReviewProxyHomogenized';
import { TrainerReviewProxyHomogenizedTypeEnum } from '@rover/rsdk/src/apiClient/latest/model/trainerReviewProxyHomogenizedTypeEnum';
import type { TrainerTestimonialHomogenized } from '@rover/rsdk/src/apiClient/latest/model/trainerTestimonialHomogenized';
import { TrainerTestimonialHomogenizedTypeEnum } from '@rover/rsdk/src/apiClient/latest/model/trainerTestimonialHomogenizedTypeEnum';

export type Replyable = AutomaticRecord | ProviderRating;

export function isProviderRating(info: ReviewDispatch): info is ProviderRating {
  return ProviderRatingTypeEnum && info.type in ProviderRatingTypeEnum;
}

export function isAutomaticRecord(info: ReviewDispatch): info is AutomaticRecord {
  return AutomaticRecordTypeEnum && info.type in AutomaticRecordTypeEnum;
}

export function isTestimonial(info: ReviewDispatch): info is Testimonial {
  return TestimonialTypeEnum && info.type in TestimonialTypeEnum;
}

export function isExternalReview(info: ReviewDispatch): info is ExternalReview {
  return ExternalReviewTypeEnum && info.type in ExternalReviewTypeEnum;
}

export function isReplyable(info: ReviewDispatch): info is Replyable {
  return info.type in AutomaticRecordTypeEnum || info.type in ProviderRatingTypeEnum;
}

export function isTrainerTestimonial(info: ReviewDispatch): info is TrainerTestimonialHomogenized {
  return (
    TrainerTestimonialHomogenizedTypeEnum && info.type in TrainerTestimonialHomogenizedTypeEnum
  );
}

export function isTrainerReview(info: ReviewDispatch): info is TrainerReviewProxyHomogenized {
  return (
    TrainerReviewProxyHomogenizedTypeEnum && info.type in TrainerReviewProxyHomogenizedTypeEnum
  );
}

/** Whether or not the object is a core service testimonial or trainer testimonial (without a rating) or external review */
export function isAnyTestimonial(
  info: ReviewDispatch
): info is Testimonial | TrainerTestimonialHomogenized | ExternalReview {
  return isTestimonial(info) || isTrainerTestimonial(info) || isExternalReview(info);
}

/** Whether or not the object is a `ProviderRating` or trainer review (aka a `TrainerTestimonial` with a rating) */
export function isAnyRating(
  info: ReviewDispatch
): info is ProviderRating | TrainerReviewProxyHomogenized {
  return isProviderRating(info) || isTrainerReview(info);
}

const iconMap: Record<string, Icon> = {
  'sitter-home': Boarding,
  'sitter-traveling': Sitting,
  homevists: DropIn,
  daycare: Daycare,
  walking: Walking,
  grooming: Grooming,
  'dog-training': DogTraining,
};

export const getIcon = (iconName: string): Icon => {
  if (iconName in iconMap) {
    return iconMap[iconName];
  }
  return Walking;
};
